import { useState, useRef, useEffect } from 'react'

const CIRCLE = 300   // preview diameter px
const OUTPUT = 256   // saved image size px

function clampOffset(x, y, zoom, nat) {
  if (!nat) return { x: 0, y: 0 }
  const bs = Math.max(CIRCLE / nat.w, CIRCLE / nat.h)
  const scale = bs * zoom
  const maxX = Math.max(0, (nat.w * scale - CIRCLE) / 2)
  const maxY = Math.max(0, (nat.h * scale - CIRCLE) / 2)
  return {
    x: Math.max(-maxX, Math.min(maxX, x)),
    y: Math.max(-maxY, Math.min(maxY, y)),
  }
}

export default function AvatarCropper({ imageSrc, onCancel, onSave }) {
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [natural, setNatural] = useState(null) // { w, h }

  // Refs for drag — avoids stale closures in global listeners
  const isDragging = useRef(false)
  const dragOrigin = useRef({ x: 0, y: 0 })   // pointer position at drag start
  const offsetSnap = useRef({ x: 0, y: 0 })   // offset at drag start
  const liveState = useRef({ zoom: 1, offset: { x: 0, y: 0 }, natural: null })
  liveState.current = { zoom, offset, natural }

  const baseScale = natural ? Math.max(CIRCLE / natural.w, CIRCLE / natural.h) : 1

  // ── Image load ──────────────────────────────────────────
  const handleImgLoad = (e) => {
    const nat = { w: e.target.naturalWidth, h: e.target.naturalHeight }
    setNatural(nat)
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }

  // ── Drag ────────────────────────────────────────────────
  const getEventPos = (e) => {
    const src = e.touches ? e.touches[0] : e
    return { x: src.clientX, y: src.clientY }
  }

  const handlePointerDown = (e) => {
    e.preventDefault()
    isDragging.current = true
    dragOrigin.current = getEventPos(e)
    offsetSnap.current = { ...liveState.current.offset }
  }

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return
      if (e.cancelable) e.preventDefault()
      const pos = getEventPos(e)
      const dx = pos.x - dragOrigin.current.x
      const dy = pos.y - dragOrigin.current.y
      const { zoom, natural } = liveState.current
      setOffset(clampOffset(
        offsetSnap.current.x + dx,
        offsetSnap.current.y + dy,
        zoom,
        natural,
      ))
    }
    const onUp = () => { isDragging.current = false }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onUp)
    }
  }, []) // mount/unmount only — reads mutable state via liveState ref

  // ── Zoom ────────────────────────────────────────────────
  const handleZoom = (v) => {
    const z = Number(v)
    setZoom(z)
    setOffset((o) => clampOffset(o.x, o.y, z, liveState.current.natural))
  }

  // ── Save ────────────────────────────────────────────────
  const handleSave = () => {
    const { zoom, offset, natural } = liveState.current
    if (!natural) return

    const canvas = document.createElement('canvas')
    canvas.width = OUTPUT
    canvas.height = OUTPUT
    const ctx = canvas.getContext('2d')

    // Circular clip
    ctx.beginPath()
    ctx.arc(OUTPUT / 2, OUTPUT / 2, OUTPUT / 2, 0, Math.PI * 2)
    ctx.clip()

    // Map preview coords → canvas coords
    const bs = Math.max(CIRCLE / natural.w, CIRCLE / natural.h)
    const ratio = OUTPUT / CIRCLE
    const scale = bs * zoom * ratio
    const imgW = natural.w * scale
    const imgH = natural.h * scale
    const cx = OUTPUT / 2 + offset.x * ratio
    const cy = OUTPUT / 2 + offset.y * ratio

    const img = new window.Image()
    img.onload = () => {
      ctx.drawImage(img, cx - imgW / 2, cy - imgH / 2, imgW, imgH)
      const dataUrl = canvas.toDataURL('image/png')
      onSave(dataUrl)
    }
    img.src = imageSrc
  }

  // ── Render ───────────────────────────────────────────────
  const scale = baseScale * zoom
  const imgStyle = natural ? {
    position: 'absolute',
    width: natural.w * scale,
    height: natural.h * scale,
    left: '50%',
    top: '50%',
    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    pointerEvents: 'none',
    display: 'block',
  } : { display: 'none' }

  return (
    <div className="cropper-overlay" onClick={onCancel}>
      <div className="cropper-modal" onClick={(e) => e.stopPropagation()}>
        <p className="cropper-modal__title">Adjust your photo</p>
        <p className="cropper-modal__hint">Drag to reposition · slider to zoom</p>

        <div
          className="cropper-modal__circle"
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
        >
          <img
            src={imageSrc}
            alt=""
            style={imgStyle}
            onLoad={handleImgLoad}
            draggable={false}
          />
        </div>

        <div className="cropper-modal__zoom">
          <span aria-hidden="true">−</span>
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            onChange={(e) => handleZoom(e.target.value)}
            aria-label="Zoom"
          />
          <span aria-hidden="true">+</span>
        </div>

        <div className="cropper-modal__actions">
          <button type="button" className="btn-primary" onClick={handleSave}>Save</button>
          <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
